'use client';

import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { siteStatic } from '@/content/siteStatic';
import { Button } from '@/components/ui/button';

// Ensure slides are always an array
const getSlides = () => {
  try {
    return siteStatic?.bannerCarousel?.slides || [];
  } catch {
    return [];
  }
};

export function BannerCarousel() {
  const slides = getSlides();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-advance carousel
  useEffect(() => {
    if (slides.length <= 1 || isHovered) return;

    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [slides.length, isHovered]);

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % slides.length);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  if (slides.length === 0) return null;

  return (
    <section
      className="relative w-full h-[280px] md:h-[480px] overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Slides Container */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
              index === activeIndex ? 'translate-x-0' : index < activeIndex ? '-translate-x-full' : 'translate-x-full'
            }`}
          >
            <img
              src={slide.imageUrl}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
            
            {/* Content */}
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="max-w-2xl">
                  {slide.badge && (
                    <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold text-white bg-[#10b981] rounded-full shadow-lg tracking-wide uppercase">
                      {slide.badge}
                    </span>
                  )}
                  <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4 leading-tight tracking-tight">
                    {slide.title}
                  </h2>
                  <p className="text-base md:text-xl lg:text-2xl text-white/95 mb-6 md:mb-8 font-medium">
                    {slide.subtitle}
                  </p>
                  <Link href={slide.href}>
                    <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg hover:shadow-xl">
                      {slide.ctaLabel}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white rounded-full p-3 md:p-4 transition-all duration-200 shadow-xl hover:shadow-2xl z-10 backdrop-blur-sm"
            aria-label="Өмнөх слайд"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-gray-900" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white rounded-full p-3 md:p-4 transition-all duration-200 shadow-xl hover:shadow-2xl z-10 backdrop-blur-sm"
            aria-label="Дараагийн слайд"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-gray-900" />
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2.5 rounded-full transition-all duration-200 ${
                index === activeIndex
                  ? 'w-10 bg-white shadow-lg'
                  : 'w-2.5 bg-white/60 hover:bg-white/80'
              }`}
              aria-label={`${index + 1} слайд руу шилжих`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

